import { Avatar, Box, Typography } from '@mui/material'
import colors from '../../constants/colors'
import selectOptions from '../../constants/selectOptions'
import { IUserProfile } from '../../pages/UsersPage/constants'
import textUtils from '../../utils/textUtils'
import { IFileObject } from '../CaseInvestigation/types'
import Dropdown from '../Dropdown'
import RemoveButton from '../RemoveButton'
import { ITeamColumns } from './types'

const formState = {
  memberRole: '',
  memberPermission: '',
  caseStatusMessage: '',
  subjects: [''],
  daysOffWork: '0',
  daysLostOnTransfer: '0',
  workplaceInjury: 'no',
  teamMembers: [],
  name: '',
  tier_level: 1,
}

const teamColumns = ({ control, removeUserFromTeam, previewOnly = false }: ITeamColumns) => [
  {
    id: 'user.username',
    label: 'Name',
    format: (value: string, row: IUserProfile) => {
      return (
        <Box sx={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
          <Avatar src={row?.profile_pic} />
          <Typography variant='labelBold'>
            {row?.user.first_name} {row?.user.last_name}
          </Typography>
        </Box>
      )
    },
  },
  {
    id: 'user.title',
    label: 'Title',
    format: (value: string, row: IUserProfile) => {
      return (
        <Box>
          <Typography variant='labelRegular' color={colors.BLACK}>
            {textUtils.humanize(row?.title ?? '')}
          </Typography>
        </Box>
      )
    },
  },
  {
    id: 'user.case_role',
    label: 'Role',
    format: (value: string, row: any) => {
      return (
        <Box>
          <Dropdown
            control={control}
            label=''
            options={selectOptions.caseRolesOptions}
            fieldName={`teamMembers[${row?.id}].case_role`}
            selectedValue={row?.case_role}
            typographyVariant='labelRegular'
            previewOnly={previewOnly}
            sx={{ minWidth: '14rem' }}
          />
        </Box>
      )
    },
  },
  {
    id: 'user.access_role',
    label: 'Access',
    format: (value: string, row: any) => {
      return (
        <Box>
          <Dropdown
            control={control}
            label=''
            options={selectOptions.accessRolesOptions}
            fieldName={`teamMembers[${row?.id}].access_role`}
            selectedValue={row?.access_role}
            typographyVariant='labelRegular'
            previewOnly={previewOnly}
            sx={{ minWidth: '8rem' }}
          />
        </Box>
      )
    },
  },
  {
    id: 'actions',
    label: '',
    align: 'right',
    format: (value: any, row: IUserProfile) => {
      return (
        <RemoveButton
          tooltipMessage={`Remove ${row?.user?.first_name} ${row?.user?.last_name} from team`}
          removeFunctionHandler={() => {
            removeUserFromTeam(row?.id)
          }}
          previewOnly={previewOnly}
        />
      )
    },
  },
]

const locationsOfIncidents = {
  facility: {
    label: 'Facility / Building',
    value: false,
  },
  field: {
    label: 'Field',
    value: false,
  },
  office: {
    label: 'Office',
    value: false,
  },
  workTravel: {
    label: 'Work Travel',
    value: false,
  },
  offSite: {
    label: 'Off-Site',
    value: false,
  },
}

const oshaTypes = {
  type1Violence: {
    label: 'Criminal Intent',
    value: false,
  },
  type2Violence: {
    label: 'Customer / Client',
    value: false,
  },
  type3Violence: {
    label: 'Worker on Worker',
    value: false,
  },
  type4Violence: {
    label: 'Personal Relationship',
    value: false,
  },
}

const oshaViolenceTypeQuestionUuid = 'ab3ac8de-83b3-11ee-a8f7-ebe9cc45f1ea'

const oshaTypeToOptionId: { [key: string]: number } = {
  type1Violence: 50,
  type2Violence: 51,
  type3Violence: 52,
  type4Violence: 53,
}

const natureOfIncidentTypes = {
  physicalAttack: {
    label: 'Physical Attack',
    value: false,
  },
  physicalWithWeapon: {
    label:
      'Physical Attack with a Weapon, Object, or Animal, including a Gun, Knife, or other object',
    value: false,
  },
  threatOfPhysicalForce: {
    label: 'Threat of Physical Force',
    value: false,
  },
  threatOfUseOfWeapon: {
    label: 'Threat of the Use of a Weapon or other object',
    value: false,
  },
  privacyCase: {
    label: 'Privacy Case',
    value: false,
  },
  other: {
    label: 'Other',
    value: false,
  },
}

const wasLawEnforcementOrSecurityContacted = {
  lawEnforcement: {
    label: 'Law Enforcement',
    value: false,
  },
  security: {
    label: 'Security',
    value: false,
  },
  both: {
    label: 'Both',
    value: false,
  },
}

const importantDates = [
  {
    label: 'Date Issue Was Reported',
    date: '2023-01-22T02:00:00Z',
  },
  {
    label: 'Date Investigation Was Opened',
    date: '2023-01-22T02:00:00Z',
  },
  {
    label: 'Date Investigation Was Closed',
    date: '2023-01-22T02:00:00Z',
  },
]

const materialColumns = () => [
  {
    id: 'preview',
    label: 'Preview',
    format: () => {
      return (
        <img
          width='64px'
          height='64px'
          src='https://icons.iconarchive.com/icons/icons8/windows-8/256/Logos-Xbox-icon.png'
        />
      )
    },
  },
  {
    id: 'filename',
    label: 'File Name',
    format: () => {
      return <Typography variant='subtitle1'>iPhonePhoto-220303-1.jpg</Typography>
    },
  },
  // {
  //   id: 'filetype',
  //   label: 'File Type',
  //   format: () => {
  //     return <Typography variant='subtitle1'>Image</Typography>
  //   },
  // },
  {
    id: 'editfile',
    label: 'Edit File',
    format: (row: IFileObject) => {
      return <div>Replace | Edit</div>
    },
  },
]

const selectStyle = {
  minWidth: '150px',
  width: '100%',
  height: '100%',
  margin: 0,
  '&.MuiSelect-outlined , .MuiOutlinedInput-input': {
    backgroundColor: colors.WHITE,
    marginTop: 0,
  },
  '&.MuiSelect-root , .MuiSelect-icon': {
    top: 'auto !important',
    right: '1.2rem',
  },
}

export default {
  formState,
  teamColumns,
  locationsOfIncidents,
  oshaTypes,
  oshaViolenceTypeQuestionUuid,
  oshaTypeToOptionId,
  natureOfIncidentTypes,
  wasLawEnforcementOrSecurityContacted,
  importantDates,
  materialColumns,
  selectStyle,
}
